import { useCallback, useContext, useEffect, useState } from 'react';
import { ClickAwayListener } from '@mui/material';
import Popper from '@mui/material/Popper';
import { useQuery } from '@tanstack/react-query';
import { Edit2, Slash } from 'react-feather';
import type { FetchProjectTerms } from '@trinity/chat/api/interfaces/projects';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import type { UseModalProps } from '@trinity/chat-components/components/Modal/index.js';
import { Modal, Header, useModal } from '@trinity/chat-components/components/Modal/index.js';
import { useAlert } from '@trinity/chat-components/components/Modal/Alert.js';
import { PopperMenu, PopperMenuItem } from '@trinity/chat-components/components/Popper/index.js';
import { Checkbox } from '@trinity/chat-components/components/presentation/index.js';
import { Button } from '@trinity/chat-components/components/presentation/Button/Button.js';
import { Input } from '@/components/presentation/Input';
import { TranscriptReplacementsContext, ProjectTermsContext } from '@/containers/Project.Terms';
import styles from './style/CorrectionHover.module.css';

type ProjectTerm = FetchProjectTerms.Response['terms'][number];

type Props = {
  term: ProjectTerm;
};

export const CorrectionHover = (props: Props) => {
  const { addConfirmedTerms, addGlobalReplacements, projectId } = useContext(TranscriptReplacementsContext);
  const { query: projectTermsQuery } = useContext(ProjectTermsContext);
  const [isOpen, setIsOpen] = useState(false);
  const [referenceElement, setReferenceElement] = useState<HTMLDivElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);
  const [_replacementState, setReplacementState] = useState<string>('');
  const apiClient = useChatApiClient();

  // Get spell-check suggestions using the existing API
  const suggestionsQuery = useQuery({
    queryKey: ['get-spell-check-suggestions', props.term.termValue, projectId],
    queryFn: async () => {
      // Get a valid transcriptId
      let transcriptId = props.term.transcriptIds?.[0];

      if (!transcriptId) {
        // For terms without transcriptIds (derived terms), we need any valid transcriptId from the project
        // The backend only uses transcriptId for validation, not actual spell-check logic
        const projectTerms = projectTermsQuery.data?.terms || [];
        const termWithTranscripts = projectTerms.find(t => t.transcriptIds?.length > 0);
        transcriptId = termWithTranscripts?.transcriptIds?.[0];

        // If still no transcriptId, skip the call (project has no transcripts)
        if (!transcriptId) {
          return { suggestions: [] };
        }
      }

      return apiClient.transcripts.getSpellCheckSuggestions({
        transcriptId,
        term: props.term.termValue,
      });
    },
    enabled: isOpen,
    placeholderData: { suggestions: [] },
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
  });

  const [toggleEditModal, EditModal] = useModal(EditTermModal);
  const [toggleAlert, ReplaceAlert, alertOpen] = useAlert();

  const onIgnore = useCallback(() => {
    addConfirmedTerms([{ term: props.term.termValue, transcriptIds: props.term.transcriptIds }]);
    setIsOpen(false);
  }, [addConfirmedTerms, props.term.termValue, props.term.transcriptIds]);

  const onReplace = useCallback((replacement: string) => {
    addGlobalReplacements([{
      originalText: props.term.termValue,
      replacementText: replacement,
      transcriptIds: props.term.transcriptIds,
      sourceEntities: props.term.entities,
    }]);
    setIsOpen(false);
    setReplacementState('');
    if (alertOpen) {
      toggleAlert();
    }
  }, [addGlobalReplacements, alertOpen, props.term.entities, props.term.termValue, props.term.transcriptIds, toggleAlert]);

  const triggerReplaceConfirmation = useCallback((replacement: string) => {
    setReplacementState(replacement);
    setIsOpen(false);
    toggleAlert();
  }, [toggleAlert]);

  const onEditText = useCallback(() => {
    toggleEditModal();
    setIsOpen(false);
  }, [toggleEditModal]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (referenceElement && !referenceElement.contains(event.target as Node) &&
        popperElement && !popperElement.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, referenceElement, popperElement]);

  return (
    <>
      <ClickAwayListener onClickAway={() => setIsOpen(false)}>
        <div>
          <div
            className={styles.hoverContainer}
            ref={setReferenceElement}>
            <Edit2
              className={styles.icon}
              size={16}
              onClick={() => setIsOpen(true)} />
            {isOpen && (
              <Popper
                open={isOpen}
                ref={setPopperElement}
                anchorEl={referenceElement}
                placement="bottom-start"
                style={{ zIndex: 1300 }}>
                <PopperMenu>
                  {suggestionsQuery.data?.suggestions?.length || suggestionsQuery.isLoading ?
                    suggestionsQuery.data.suggestions.map(s => (
                      <PopperMenuItem key={s} onClick={() => triggerReplaceConfirmation(s)}>
                        {`"${s}"`}
                      </PopperMenuItem>
                    )) :
                    <PopperMenuItem disabled={true}>No suggestions available</PopperMenuItem>
                  }
                  <PopperMenuItem className={styles.menuItem} onClick={onIgnore}>
                    <Slash size={16} /> Ignore
                  </PopperMenuItem>
                  <PopperMenuItem className={styles.menuItem} onClick={onEditText}>
                    <Edit2 size={16} /> Edit Text
                  </PopperMenuItem>
                </PopperMenu>
              </Popper>
            )}
          </div>
          <EditModal term={props.term} />
          <ReplaceAlert
            className={styles.alert}
            message={<AlertConfirmationContent replacementText={_replacementState} term={props.term} />}
            confirmText='Replace All'
            onConfirm={() => onReplace(_replacementState)} />
        </div>
      </ClickAwayListener>
    </>
  );
};

const EditTermModal = ({ onClose, open, ...props }: Props & UseModalProps) => {
  const [state, setState] = useState(props.term.termValue);
  const [addToDict, toggleAddToDict] = useState(false);
  const { addGlobalReplacements, addDictionaryWord } = useContext(TranscriptReplacementsContext);

  const onChange = useCallback(() => {
    // Only add global replacement if the text is actually changing
    if (state !== props.term.termValue) {
      addGlobalReplacements([{
        originalText: props.term.termValue,
        replacementText: state,
        transcriptIds: props.term.transcriptIds,
        sourceEntities: props.term.entities,
      }]);
    }

    if (addToDict) {
      addDictionaryWord(state);
    }
    onClose();
  }, [addDictionaryWord, addGlobalReplacements, addToDict, onClose, props.term.entities, props.term.termValue, props.term.transcriptIds, state]);

  useEffect(() => {
    if (!isValidDictionaryTerm(state)) {
      toggleAddToDict(false);
    }
  }, [state]);

  const updateText = props.term.transcriptIds?.length ?
    `This will update ${props.term.occurrences} references across ${props.term.transcriptIds.length} transcripts.` :
    `This will update all references across all the project transcripts.`;

  return (
    <Modal open={open} onClose={onClose}>
      <Header>Update all instances of this term</Header>
      <div className={styles.row}>
        <div className={styles.label}>Selection:</div>
        <Input value={props.term.termValue} disabled />
      </div>
      <div className={styles.row}>
        <div className={styles.label}>Replace with:</div>
        <Input value={state} onChange={e => setState(e.target.value)} />
      </div>
      {isValidDictionaryTerm(props.term.termValue) &&
        <label className={styles.dictRow}>
          <Checkbox
            checked={addToDict}
            onChange={() => toggleAddToDict(!addToDict)}
            disabled={!isValidDictionaryTerm(state)} />
          <div>
            Add to medical dictionary
          </div>
        </label>
      }
      <div className={styles.updateText}>{updateText}</div>
      <div className={styles.buttons}>
        <Button variant="pill" color="secondary" onClick={onClose}>Cancel</Button>
        <Button variant="pill" color="primary" onClick={onChange}>Update</Button>
      </div>
    </Modal>
  );
};

function isValidDictionaryTerm(text: string) {
  return !text.includes(' ');
}

type AlertConfirmationProps = {
  term: ProjectTerm;
  replacementText: string;
};

const AlertConfirmationContent = ({ term, replacementText }: AlertConfirmationProps) => {
  return (
    <div>
      <Header>Replace all Terms?</Header>
      <div className={styles.confirmationLine}>Would you like to change <span className={styles.bold}>{term.termValue}</span> to <span className={styles.bold}>{replacementText}</span> across all transcripts?</div>
      {term.transcriptIds?.length && <div>This will update {term.occurrences} references across {term.transcriptIds.length} transcripts.</div>}
    </div>
  );
};